import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchMetadataTypes,
  createMetadataType,
  updateMetadataType,
  deleteMetadataType,
  fetchMetadataItems,
  createMetadataItem,
  updateMetadataItem,
  deleteMetadataItem,
  fetchPrompts,
  createPrompt,
  updatePrompt,
  deletePrompt
} from '../api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Skeleton } from './ui/skeleton';

const promptTypes = [
  { id: 'Upsell', fallbackLabel: 'Допродажа', metadataKey: 'upsell' },
  { id: 'Checklist', fallbackLabel: 'Чек-лист', metadataKey: 'rules' },
  { id: 'Negativity', fallbackLabel: 'Негатив', metadataKey: 'negativity' },
  { id: 'Classification', fallbackLabel: 'Классификация', metadataKey: 'callclasses' },
];

const MAX_EXTRACTION_RULES = 5;
const MAX_EXAMPLES = 3;

const createRuleClientId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const createExampleClientId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const emptyItemForm = { name: '', description: '', instructions: '', isSuccessfulResult: false };

const createEmptyExtractionRule = () => ({
  clientId: createRuleClientId(),
  variableName: '',
  pattern: '',
  isRequired: false
});

const createEmptyPromptExample = () => ({
  clientId: createExampleClientId(),
  question: '',
  answer: ''
});

const createEmptyPromptForm = () => ({
  name: '',
  type: promptTypes[0].id,
  systemPrompt: '',
  userPrompt: '{{TEXT}}',
  order: 10,
  isEnabled: true,
  model: '',
  extractionRules: [createEmptyExtractionRule()],
  examples: []
});

const emptyTypeForm = { key: '', displayName: '', placeholder: '', order: 0 };

const TableRowsSkeleton = ({ columns, rows = 3 }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <TableRow key={rowIndex}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <TableCell key={colIndex}>
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const MetadataTabsSkeleton = () => (
  <div className="space-y-6">
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} className="h-9 w-32" />
      ))}
    </div>

    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-32" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
      </CardContent>
    </Card>

    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-9 w-20" />
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Название</TableHead>
                <TableHead>Описание</TableHead>
                <TableHead>Инструкции</TableHead>
                <TableHead className="w-[140px] text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRowsSkeleton columns={4} />
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  </div>
);

const MetadataManager = () => {
  const { toast } = useToast();
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [activeType, setActiveType] = useState(null);
  const [typeDrafts, setTypeDrafts] = useState({});
  const [itemsByType, setItemsByType] = useState({});
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptForm, setPromptForm] = useState(createEmptyPromptForm);
  const [prompts, setPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [createTypeDialogOpen, setCreateTypeDialogOpen] = useState(false);
  const [createTypeForm, setCreateTypeForm] = useState(emptyTypeForm);

  useEffect(() => {
    loadTypes();
    loadPrompts();
  }, []);

  useEffect(() => {
    if (activeType) {
      loadItems(activeType);
    }
  }, [activeType]);

  const { promptTypeOptions, promptTypeLabelMap } = useMemo(() => {
    const byKey = types.reduce((acc, type) => {
      const key = type.key?.toLowerCase();
      if (key) {
        acc[key] = type.displayName || type.key;
      }
      return acc;
    }, {});

    const labelMap = {};
    const options = promptTypes.map((type) => {
      const label = byKey[type.metadataKey] ?? type.fallbackLabel;
      labelMap[type.id] = label;
      return { id: type.id, label };
    });

    return { promptTypeOptions: options, promptTypeLabelMap: labelMap };
  }, [types]);

  const currentType = useMemo(
    () => types.find((type) => type.key === activeType),
    [types, activeType]
  );

  const loadTypes = async () => {
    setTypesLoading(true);
    try {
      const data = await fetchMetadataTypes();
      setTypes(data);
      setTypeDrafts(
        data.reduce((acc, type) => {
          acc[type.key] = {
            displayName: type.displayName,
            placeholder: type.placeholder,
            order: type.order
          };
          return acc;
        }, {})
      );
      if (!activeType && data.length > 0) {
        setActiveType(data[0].key);
      } else if (activeType && !data.find((type) => type.key === activeType) && data.length > 0) {
        setActiveType(data[0].key);
      }
    } catch {
      toast({ title: 'Не удалось загрузить типы метаданных', variant: 'destructive' });
    } finally {
      setTypesLoading(false);
    }
  };

  const loadItems = async (typeKey) => {
    setItemsLoading(true);
    try {
      const data = await fetchMetadataItems(typeKey);
      setItemsByType((prev) => ({ ...prev, [typeKey]: data }));
    } catch {
      toast({ title: 'Не удалось загрузить элементы', variant: 'destructive' });
    } finally {
      setItemsLoading(false);
    }
  };

  const loadPrompts = async () => {
    setPromptsLoading(true);
    try {
      const data = await fetchPrompts(true);
      setPrompts(
        data.map((prompt) => ({
          ...prompt,
          extractionRules: (prompt.extractionRules ?? []).map((rule) => ({
            ...rule,
            clientId: rule.clientId ?? createRuleClientId()
          })),
          examples: (prompt.examples ?? []).slice(0, MAX_EXAMPLES).map((example) => ({
            ...example,
            clientId: example.clientId ?? createExampleClientId()
          }))
        }))
      );
    } catch {
      toast({ title: 'Не удалось загрузить инструкции', variant: 'destructive' });
    } finally {
      setPromptsLoading(false);
    }
  };

  const handleOpenItemDialog = (item = null) => {
    setEditingItem(item);
    if (item) {
      setItemForm({
        name: item.name ?? '',
        description: item.description ?? '',
        instructions: item.instructions ?? '',
        isSuccessfulResult: Boolean(item.isSuccessfulResult)
      });
    } else {
      setItemForm(emptyItemForm);
    }
    setItemDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!activeType || itemForm.name.trim().length === 0) {
      return;
    }

    const payload = {
      name: itemForm.name.trim(),
      description: itemForm.description ?? '',
      instructions: itemForm.instructions ?? ''
    };

    if ((activeType ?? '').toLowerCase() === 'results') {
      payload.isSuccessfulResult = Boolean(itemForm.isSuccessfulResult);
    }

    try {
      if (editingItem) {
        await updateMetadataItem(activeType, editingItem.id, payload);
        toast({ title: 'Запись обновлена' });
      } else {
        await createMetadataItem(activeType, payload);
        toast({ title: 'Запись создана' });
      }
      setItemDialogOpen(false);
      setEditingItem(null);
      setItemForm(emptyItemForm);
      await loadItems(activeType);
    } catch {
      toast({ title: 'Не удалось сохранить запись', variant: 'destructive' });
    }
  };

  const handleDeleteItem = async (item) => {
    if (!activeType || !item) {
      return;
    }

    if (!window.confirm(`Удалить "${item.name}"?`)) {
      return;
    }

    try {
      await deleteMetadataItem(activeType, item.id);
      toast({ title: 'Запись удалена' });
      await loadItems(activeType);
    } catch {
      toast({ title: 'Не удалось удалить запись', variant: 'destructive' });
    }
  };

  const handleTypeDraftChange = (key, field, value) => {
    setTypeDrafts((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleSaveTypeSettings = async () => {
    if (!currentType) {
      return;
    }

    const draft = typeDrafts[currentType.key];
    try {
      await updateMetadataType(currentType.key, {
        displayName: draft.displayName,
        placeholder: draft.placeholder,
        order: draft.order
      });
      toast({ title: 'Тип обновлен' });
      await loadTypes();
    } catch (error) {
      const message =
        error?.response?.data ?? 'Не удалось сохранить настройки типа. Проверьте данные.';
      toast({ title: message, variant: 'destructive' });
    }
  };

  const handleDeleteType = async (type) => {
    if (!type || type.isSystem || type.isRequired) {
      return;
    }

    if (
      !window.confirm(
        `Удалить тип "${type.displayName}"? Все связанные элементы будут удалены безвозвратно.`
      )
    ) {
      return;
    }

    try {
      await deleteMetadataType(type.key);
      toast({ title: 'Тип удален' });
      if (activeType === type.key) {
        setActiveType(null);
      }
      await loadTypes();
    } catch {
      toast({ title: 'Не удалось удалить тип', variant: 'destructive' });
    }
  };

  const handleCreateType = async () => {
    if (
      !createTypeForm.key.trim() ||
      !createTypeForm.displayName.trim() ||
      !createTypeForm.placeholder.trim()
    ) {
      return;
    }

    try {
      await createMetadataType({
        key: createTypeForm.key.trim().toLowerCase(),
        displayName: createTypeForm.displayName.trim(),
        placeholder: createTypeForm.placeholder.trim(),
        order: Number(createTypeForm.order) || 0
      });

      toast({ title: 'Тип создан' });
      setCreateTypeDialogOpen(false);
      setCreateTypeForm(emptyTypeForm);
      await loadTypes();
    } catch (error) {
      const message = error?.response?.data ?? 'Не удалось создать тип';
      toast({ title: message, variant: 'destructive' });
    }
  };

  const handleOpenPromptDialog = (prompt = null) => {
    setEditingPrompt(prompt);
    setPromptForm(
      prompt
        ? {
            name: prompt.name,
            type: prompt.type,
            systemPrompt: prompt.systemPrompt,
            userPrompt: prompt.userPrompt,
            order: prompt.order,
            isEnabled: prompt.isEnabled,
            model: prompt.model ?? '',
            extractionRules:
              (prompt.extractionRules?.length
                ? prompt.extractionRules
                : [createEmptyExtractionRule()]
              )
                .slice(0, MAX_EXTRACTION_RULES)
                .map((rule) => ({
                  clientId: rule.clientId ?? createRuleClientId(),
                  variableName: rule.variableName ?? '',
                  pattern: rule.pattern ?? '',
                  isRequired: Boolean(rule.isRequired)
                })),
            examples: (prompt.examples ?? [])
              .slice(0, MAX_EXAMPLES)
              .map((example) => ({
                clientId: example.clientId ?? createExampleClientId(),
                question: example.question ?? '',
                answer: example.answer ?? ''
              }))
          }
        : createEmptyPromptForm()
    );
    setPromptDialogOpen(true);
  };

  const handleAddExtractionRule = () => {
    setPromptForm((prev) => {
      const current = prev.extractionRules ?? [];
      if (current.length >= MAX_EXTRACTION_RULES) {
        return prev;
      }
      return {
        ...prev,
        extractionRules: [...current, createEmptyExtractionRule()]
      };
    });
  };

  const handleUpdateExtractionRule = (index, key, value) => {
    setPromptForm((prev) => {
      const current = prev.extractionRules ?? [];
      const next = current.map((rule, idx) =>
        idx === index
          ? {
              ...rule,
              [key]: key === 'isRequired' ? Boolean(value) : value
            }
          : rule
      );
      return { ...prev, extractionRules: next };
    });
  };

  const handleRemoveExtractionRule = (index) => {
    setPromptForm((prev) => {
      const current = prev.extractionRules ?? [];
      const next = current.filter((_, idx) => idx !== index);
      return {
        ...prev,
        extractionRules: next.length ? next : [createEmptyExtractionRule()]
      };
    });
  };

  const handleAddExample = () => {
    setPromptForm((prev) => {
      const current = prev.examples ?? [];
      if (current.length >= MAX_EXAMPLES) {
        return prev;
      }

      return {
        ...prev,
        examples: [...current, createEmptyPromptExample()]
      };
    });
  };

  const handleUpdateExample = (index, key, value) => {
    setPromptForm((prev) => {
      const current = prev.examples ?? [];
      const next = current.map((example, idx) =>
        idx === index
          ? {
              ...example,
              [key]: value
            }
          : example
      );

      return { ...prev, examples: next };
    });
  };

  const handleRemoveExample = (index) => {
    setPromptForm((prev) => ({
      ...prev,
      examples: (prev.examples ?? []).filter((_, idx) => idx !== index)
    }));
  };

  const handleSavePrompt = async () => {
    if (promptForm.name.trim().length === 0) {
      return;
    }

    const normalizedRules = promptForm.extractionRules ?? [];
    const incompleteRule = normalizedRules.find(
      (rule) =>
        (rule.variableName?.trim() && !rule.pattern?.trim()) ||
        (!rule.variableName?.trim() && rule.pattern?.trim())
    );
    if (incompleteRule) {
      toast({
        title: 'Заполните название и регулярное выражение для каждой переменной',
        variant: 'destructive'
      });
      return;
    }

    const normalizedExamples = promptForm.examples ?? [];
    const incompleteExample = normalizedExamples.find(
      (example) =>
        (example.question?.trim() && !example.answer?.trim()) ||
        (!example.question?.trim() && example.answer?.trim())
    );
    if (incompleteExample) {
      toast({
        title: 'Заполните и вопрос, и ответ для каждого примера',
        variant: 'destructive'
      });
      return;
    }

    const extractionRulesPayload = normalizedRules
      .map((rule) => ({
        variableName: rule.variableName?.trim() ?? '',
        pattern: rule.pattern?.trim() ?? '',
        isRequired: Boolean(rule.isRequired)
      }))
      .filter((rule) => rule.variableName && rule.pattern);

    const examplesPayload = normalizedExamples
      .map((example) => ({
        question: example.question?.trim() ?? '',
        answer: example.answer?.trim() ?? ''
      }))
      .filter((example) => example.question && example.answer);

    const payload = {
      name: promptForm.name.trim(),
      type: promptForm.type,
      systemPrompt: promptForm.systemPrompt ?? '',
      userPrompt: promptForm.userPrompt ?? '',
      order: Number(promptForm.order) || 0,
      isEnabled: promptForm.isEnabled,
      model: promptForm.model?.trim() ? promptForm.model.trim() : null,
      extractionRules: extractionRulesPayload,
      examples: examplesPayload
    };

    try {
      if (editingPrompt) {
        await updatePrompt(editingPrompt.id, payload);
        toast({ title: 'Инструкция обновлена' });
      } else {
        await createPrompt(payload);
        toast({ title: 'Инструкция создана' });
      }
      setPromptDialogOpen(false);
      setEditingPrompt(null);
      setPromptForm(createEmptyPromptForm());
      await loadPrompts();
    } catch {
      toast({ title: 'Не удалось сохранить инструкцию', variant: 'destructive' });
    }
  };

  const handleDeletePrompt = async (prompt) => {
    if (!window.confirm(`Удалить инструкцию "${prompt.name}"?`)) {
      return;
    }

    try {
      await deletePrompt(prompt.id);
      toast({ title: 'Инструкция удалена' });
      await loadPrompts();
    } catch {
      toast({ title: 'Не удалось удалить инструкцию', variant: 'destructive' });
    }
  };

  const handleTogglePrompt = async (prompt) => {
    const payload = {
      name: prompt.name,
      type: prompt.type,
      systemPrompt: prompt.systemPrompt,
      userPrompt: prompt.userPrompt,
      order: prompt.order,
      isEnabled: !prompt.isEnabled,
      model: prompt.model ?? null,
      extractionRules: (prompt.extractionRules ?? []).map((rule) => ({
        variableName: rule.variableName ?? '',
        pattern: rule.pattern ?? '',
        isRequired: Boolean(rule.isRequired)
      })),
      examples: (prompt.examples ?? []).map((example) => ({
        question: example.question ?? '',
        answer: example.answer ?? ''
      }))
    };

    try {
      await updatePrompt(prompt.id, payload);
      await loadPrompts();
    } catch {
      toast({ title: 'Не удалось обновить инструкцию', variant: 'destructive' });
    }
  };

  const handleReorderPrompt = async (promptId, direction) => {
    const sorted = [...prompts].sort((a, b) => (a.order === b.order ? a.id - b.id : a.order - b.order));
    const index = sorted.findIndex((p) => p.id === promptId);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (index === -1 || targetIndex < 0 || targetIndex >= sorted.length) {
      return;
    }

    const current = sorted[index];
    const neighbor = sorted[targetIndex];

    try {
      await Promise.all([
        updatePrompt(current.id, { ...current, order: neighbor.order }),
        updatePrompt(neighbor.id, { ...neighbor, order: current.order })
      ]);
      await loadPrompts();
    } catch {
      toast({ title: 'Не удалось изменить порядок', variant: 'destructive' });
    }
  };

  const currentItems = itemsByType[activeType] ?? [];
  const currentDraft = currentType ? typeDrafts[currentType.key] : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Метаданные и инструкции</h2>
          <p className="text-muted-foreground">
            Управляйте типами метаданных, placeholders и последовательностью инструкций.
          </p>
        </div>
        <Button onClick={() => setCreateTypeDialogOpen(true)}>Добавить тип</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Типы метаданных</CardTitle>
        </CardHeader>
        <CardContent>
          {typesLoading && <MetadataTabsSkeleton />}
          {!typesLoading && types.length === 0 && (
            <p className="text-sm text-muted-foreground">Типы пока не созданы.</p>
          )}

          {types.length > 0 && !typesLoading && (
            <Tabs value={activeType ?? ''} onValueChange={setActiveType}>
              <TabsList className="flex flex-wrap">
                {types.map((type) => (
                  <TabsTrigger key={type.key} value={type.key} className="capitalize">
                    {type.displayName || type.key}
                    {type.isSystem && (
                      <Badge className="ml-2" variant="outline">
                        системный
                      </Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>

              {types.map((type) => (
                <TabsContent key={type.key} value={type.key} className="mt-4 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Настройки типа</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label>Отображаемое название</Label>
                          <Input
                            value={typeDrafts[type.key]?.displayName ?? ''}
                            onChange={(event) =>
                              handleTypeDraftChange(type.key, 'displayName', event.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Placeholder</Label>
                          <Input
                            value={typeDrafts[type.key]?.placeholder ?? ''}
                            onChange={(event) =>
                              handleTypeDraftChange(type.key, 'placeholder', event.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Порядок</Label>
                          <Input
                            type="number"
                            value={typeDrafts[type.key]?.order ?? 0}
                            onChange={(event) =>
                              handleTypeDraftChange(type.key, 'order', Number(event.target.value))
                            }
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveTypeSettings} disabled={!currentDraft}>
                          Сохранить
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={type.isSystem || type.isRequired}
                          onClick={() => handleDeleteType(type)}
                        >
                          Удалить
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Элементы</h3>
                      <Button onClick={() => handleOpenItemDialog()} size="sm">
                        Добавить
                      </Button>
                    </div>
                    <Card>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Название</TableHead>
                              <TableHead>Описание</TableHead>
                              <TableHead>Инструкции</TableHead>
                          <TableHead className="w-[140px] text-right">Действия</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {itemsLoading && activeType === type.key && (
                          <TableRowsSkeleton columns={4} />
                        )}
                        {!itemsLoading &&
                          (itemsByType[type.key] ?? []).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      <span>{item.name}</span>
                                      {type.key.toLowerCase() === 'results' && item.isSuccessfulResult && (
                                        <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50 dark:text-green-200 dark:border-green-800 dark:bg-green-900/30">
                                          успех
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {item.description || '—'}
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {item.instructions || '—'}
                                  </TableCell>
                                  <TableCell className="text-right space-x-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleOpenItemDialog(item)}
                                    >
                                      Изменить
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600"
                                      onClick={() => handleDeleteItem(item)}
                                    >
                                      Удалить
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            {!itemsLoading && (itemsByType[type.key] ?? []).length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-muted-foreground">
                                  Данных пока нет
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Инструкции</CardTitle>
            <p className="text-sm text-muted-foreground">
              Управляйте порядком, включением и текстами сообщений.
            </p>
          </div>
          <Button onClick={() => handleOpenPromptDialog()}>Добавить инструкцию</Button>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Порядок</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right w-[220px]">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {promptsLoading && <TableRowsSkeleton columns={5} rows={4} />}
            {!promptsLoading &&
              prompts
                .sort((a, b) => (a.order === b.order ? a.id - b.id : a.order - b.order))
                .map((prompt, index, arr) => (
                      <TableRow key={prompt.id}>
                        <TableCell className="font-medium">{prompt.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{promptTypeLabelMap[prompt.type] ?? prompt.type}</Badge>
                        </TableCell>
                        <TableCell>{prompt.order}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={prompt.isEnabled}
                              onCheckedChange={() => handleTogglePrompt(prompt)}
                            />
                            <span className="text-sm text-muted-foreground">
                              {prompt.isEnabled ? 'Включен' : 'Выключен'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="space-x-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={index === 0}
                            onClick={() => handleReorderPrompt(prompt.id, 'up')}
                          >
                            ↑
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={index === arr.length - 1}
                            onClick={() => handleReorderPrompt(prompt.id, 'down')}
                          >
                            ↓
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenPromptDialog(prompt)}
                          >
                            Изменить
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeletePrompt(prompt)}
                          >
                            Удалить
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                {!promptsLoading && prompts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Инструкции не найдены
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Изменение записи' : 'Новая запись'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Название</Label>
              <Input
                value={itemForm.name}
                onChange={(event) => setItemForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div>
              <Label>Описание</Label>
              <Textarea
                rows={3}
                value={itemForm.description}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </div>
            <div>
              <Label>Инструкции</Label>
              <Textarea
                rows={4}
                value={itemForm.instructions}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, instructions: event.target.value }))
                }
              />
            </div>
            {(activeType ?? '').toLowerCase() === 'results' && (
              <div className="flex items-center space-x-2 pt-1">
                <Switch
                  checked={itemForm.isSuccessfulResult}
                  onCheckedChange={(value) =>
                    setItemForm((prev) => ({ ...prev, isSuccessfulResult: value === true }))
                  }
                />
                <span className="text-sm">
                  Учитывать как положительный результат для конверсии
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSaveItem} disabled={itemForm.name.trim().length === 0}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promptDialogOpen} onOpenChange={setPromptDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPrompt ? 'Изменение инструкции' : 'Новая инструкция'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Название</Label>
              <Input
                value={promptForm.name}
                onChange={(event) =>
                  setPromptForm((prev) => ({ ...prev, name: event.target.value }))
                }
              />
            </div>
            <div className="grid gap-2 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Тип</Label>
                <Select
                  value={promptForm.type}
                  onValueChange={(value) => setPromptForm((prev) => ({ ...prev, type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {promptTypeOptions.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Порядок</Label>
                <Input
                  type="number"
                  value={promptForm.order}
                  onChange={(event) =>
                    setPromptForm((prev) => ({ ...prev, order: Number(event.target.value) }))
                  }
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  checked={promptForm.isEnabled}
                  onCheckedChange={(value) =>
                    setPromptForm((prev) => ({ ...prev, isEnabled: value }))
                  }
                />
                <span className="text-sm">Активен</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="promptModel">Модель</Label>
              <Input
                id="promptModel"
                placeholder="По умолчанию из настроек"
                value={promptForm.model || ''}
                onChange={(event) =>
                  setPromptForm((prev) => ({ ...prev, model: event.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>System prompt</Label>
              <Textarea
                rows={6}
                value={promptForm.systemPrompt}
                onChange={(event) =>
                  setPromptForm((prev) => ({ ...prev, systemPrompt: event.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>User prompt</Label>
              <Textarea
                rows={6}
                value={promptForm.userPrompt}
                onChange={(event) =>
                  setPromptForm((prev) => ({ ...prev, userPrompt: event.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Используйте placeholders {'{{TEXT}}'} для транскрипта, {'{{THEME}}'} и {'{{OUTCOME}}'} для
                результатов предыдущих шагов.
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Label>Переменные из результатов моделей</Label>
                  <p className="text-xs text-muted-foreground">
                    До {MAX_EXTRACTION_RULES} переменных. Значение берётся из первой группы совпадения, иначе из
                    полного совпадения.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddExtractionRule}
                  disabled={(promptForm.extractionRules?.length ?? 0) >= MAX_EXTRACTION_RULES}
                >
                  Добавить
                </Button>
              </div>
              <div className="space-y-3">
                {(promptForm.extractionRules ?? []).map((rule, index) => (
                  <div
                    key={rule.clientId ?? `${index}`}
                    className="rounded-md border p-3 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">
                        Переменная #{index + 1}
                      </span>
                      {promptForm.extractionRules.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveExtractionRule(index)}
                        >
                          Удалить
                        </Button>
                      )}
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-1">
                        <Label>Название переменной</Label>
                        <Input
                          value={rule.variableName}
                          onChange={(event) =>
                            handleUpdateExtractionRule(index, 'variableName', event.target.value)
                          }
                          placeholder="например, resultType"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Регулярное выражение</Label>
                        <Input
                          value={rule.pattern}
                          onChange={(event) =>
                            handleUpdateExtractionRule(index, 'pattern', event.target.value)
                          }
                          placeholder="Например, Итог:\\s*(.*)"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`rule-required-${index}`}
                        checked={rule.isRequired}
                        onCheckedChange={(value) =>
                          handleUpdateExtractionRule(index, 'isRequired', value === true)
                        }
                      />
                      <label
                        htmlFor={`rule-required-${index}`}
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        Значение обязательно
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Label>Примеры</Label>
                  <p className="text-xs text-muted-foreground">
                    До {MAX_EXAMPLES} few-shot примеров, которые будут добавлены перед финальным запросом.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddExample}
                  disabled={(promptForm.examples?.length ?? 0) >= MAX_EXAMPLES}
                >
                  Добавить пример
                </Button>
              </div>
              {(promptForm.examples?.length ?? 0) === 0 && (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  Примеры не добавлены.
                </div>
              )}
              <div className="space-y-3">
                {(promptForm.examples ?? []).map((example, index) => (
                  <div
                    key={example.clientId ?? `${index}`}
                    className="rounded-md border p-3 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">
                        Пример #{index + 1}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveExample(index)}
                      >
                        Удалить
                      </Button>
                    </div>
                    <div className="space-y-1">
                      <Label>Вопрос</Label>
                      <Textarea
                        rows={4}
                        value={example.question}
                        onChange={(event) =>
                          handleUpdateExample(index, 'question', event.target.value)
                        }
                        placeholder="Сообщение пользователя"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Ответ</Label>
                      <Textarea
                        rows={4}
                        value={example.answer}
                        onChange={(event) =>
                          handleUpdateExample(index, 'answer', event.target.value)
                        }
                        placeholder="Ожидаемый ответ модели"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setPromptDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSavePrompt} disabled={promptForm.name.trim().length === 0}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createTypeDialogOpen} onOpenChange={setCreateTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новый тип метаданных</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ключ (латиница)</Label>
              <Input
                value={createTypeForm.key}
                onChange={(event) =>
                  setCreateTypeForm((prev) => ({ ...prev, key: event.target.value }))
                }
                placeholder="например, followup"
              />
            </div>
            <div>
              <Label>Отображаемое название</Label>
              <Input
                value={createTypeForm.displayName}
                onChange={(event) =>
                  setCreateTypeForm((prev) => ({ ...prev, displayName: event.target.value }))
                }
              />
            </div>
            <div>
              <Label>Placeholder</Label>
              <Input
                value={createTypeForm.placeholder}
                onChange={(event) =>
                  setCreateTypeForm((prev) => ({ ...prev, placeholder: event.target.value }))
                }
              />
            </div>
            <div>
              <Label>Порядок</Label>
              <Input
                type="number"
                value={createTypeForm.order}
                onChange={(event) =>
                  setCreateTypeForm((prev) => ({
                    ...prev,
                    order: Number(event.target.value)
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTypeDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleCreateType}>Создать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetadataManager;
